import { Minus, Plus, Trash2, ShoppingCart, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { useFormatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "@/components/ui/sheet";
import { useAuthGate } from "@/hooks/useAuthGate";

export default function CartDrawer() {
  const { t, language } = useLanguage();
  const { cartData, removeItem, updateQuantity, itemsCount, total, isMutating } = useCart();
  const formatPrice = useFormatPrice();
  const navigate = useNavigate();
  const { requireAuth } = useAuthGate();


  if (itemsCount === 0) return null;

  const items = cartData.items;
 

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          size="lg"
          className="fixed bottom-6 end-6 z-40 gap-2 rounded-full shadow-xl px-6 py-6 text-base font-bold"
        >
          <ShoppingCart className="h-5 w-5" />
          <span>{itemsCount}</span>
          <span className="mx-1">·</span>
          {/* <span>{formatPrice(total)}</span> */}
          <span>{formatPrice(cartData.subtotal)}</span>
        </Button>
      </SheetTrigger>

      <SheetContent side={language === "ar" ? "left" : "right"} className="flex flex-col w-full sm:max-w-md p-0">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
          <SheetTitle className="font-heading text-xl flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-primary" />
            {t("cart.title")} ({itemsCount})
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="flex-1 px-5">
          <div className="py-3 space-y-1">
            {items.map((item) => {
              const name = language === "ar" ? item.name_ar : item.name;
              return (
                <div key={item.id} className="flex gap-3 py-3 border-b border-border last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-1">
                      <h4 className="font-heading font-bold text-foreground text-sm truncate">{name}</h4>
                      <button
                        onClick={() => removeItem(item.id)}
                        disabled={isMutating}
                        className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-0.5 disabled:opacity-50"
                        aria-label="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="mt-1 flex flex-wrap gap-1">
                      {item.customizations.feature_summary.map((group) =>
                        group.values.map((value) => (
                          <Badge key={`${group.groupKey}-${value}`} variant={group.groupKey === "sizes" ? "secondary" : "outline"} className="text-[10px] px-1.5 py-0">
                            {group.groupLabel}: {value}
                          </Badge>
                        ))
                      )}
                      {item.customizations.removed_ingredients.map((r) => (
                        <Badge key={r} variant="destructive" className="text-[10px] px-1.5 py-0 line-through">{r}</Badge>
                      ))}
                    </div>
                    {item.customizations.special_instructions && (
                      <p className="text-[11px] text-muted-foreground mt-0.5 italic truncate">
                        "{item.customizations.special_instructions}"
                      </p>
                    )}

                    {/* Qty + Price */}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1.5 rounded-md border border-border p-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          disabled={isMutating}
                          onClick={() => item.quantity <= 1 ? removeItem(item.id) : updateQuantity(item.id, item.quantity - 1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-5 text-center text-xs font-bold text-foreground">{item.quantity}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          disabled={isMutating}
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <span className="font-heading font-bold text-primary text-sm">
                        {formatPrice(item.total_price)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <SheetFooter className="border-t border-border px-5 py-4 flex-col gap-3">
          <div className="flex items-center justify-between w-full">
            <span className="font-heading font-bold text-foreground text-lg">{t("cart.subtotal")}</span>
            <span className="font-heading font-bold text-primary text-xl">{formatPrice(cartData.subtotal)}</span>
          </div>
          <Button
            size="lg"
            className="w-full gap-2 py-5 text-base font-bold rounded-xl"
             onClick={() => requireAuth(() =>  navigate("/cart"))}
            
          >
            {t("cart.title")}
            <ArrowRight className="h-5 w-5" />
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
